import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

// Currency multipliers for threshold adjustments
const getCurrencyMultiplier = (currency) => {
  const multipliers = {
    // North America
    USD: 1, CAD: 1.35,
    // Europe
    EUR: 0.92, GBP: 0.79, CHF: 0.88,
    // Latin America
    BRL: 5.0, MXN: 17.0, ARS: 350, COP: 4000, CLP: 900,
    // Asia-Pacific
    INR: 83, JPY: 150, AUD: 1.55, SGD: 1.35, MYR: 4.7,
    THB: 36, IDR: 15500, PHP: 56, VND: 24000, KRW: 1300,
    CNY: 7.2, PKR: 280, BDT: 110,
    // Africa
    NGN: 1500, ZAR: 18, KES: 155, EGP: 31, GHS: 12,
    TZS: 2500, UGX: 3800, XOF: 600, MAD: 10,
    // Middle East
    AED: 3.67, SAR: 3.75, ILS: 3.7, QAR: 3.64
  };
  return multipliers[currency] || 1;
};

// Get currency-adjusted threshold
const getAdjustedThreshold = (baseUSD, currency) => {
  return baseUSD * getCurrencyMultiplier(currency);
};

// Helper function for formatting currency
const formatCurrency = (amount, currency = 'USD') => {
  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', AUD: 'A$', CAD: 'C$',
    CHF: 'CHF', CNY: '¥', INR: '₹', BRL: 'R$', MXN: '$', ZAR: 'R',
    AED: 'د.إ', NGN: '₦', KES: 'KSh', GHS: '₵', EGP: 'E£', default: ''
  };
  const symbol = currencySymbols[currency] || currencySymbols.default;
  const formattedAmount = amount?.toLocaleString(undefined, {
    minimumFractionDigits: 0, maximumFractionDigits: 2
  }) || '0';
  return currency && !currencySymbols[currency] 
    ? `${formattedAmount} ${currency}` : `${symbol}${formattedAmount}`;
};

// Safe number formatter - prevents "00" and handles null/undefined
const safeNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) return null;
  const num = parseFloat(value);
  if (num === 0) return '0';
  return num.toFixed(decimals);
};

// Format percentage safely
const formatPercent = (value) => {
  const num = safeNumber(value, 2);
  return num !== null ? `${num}%` : 'N/A';
};

// Format frequency safely
const formatFrequency = (value) => {
  const num = safeNumber(value, 1);
  return num !== null ? `${num}x` : 'N/A';
};

// Get objective category for logic decisions
const getObjectiveCategory = (objective) => {
  if (!objective) return 'SALES';
  const upperObjective = objective.toUpperCase();
  
  if (['OUTCOME_TRAFFIC', 'LINK_CLICKS', 'LANDING_PAGE_VIEWS'].some(o => upperObjective.includes(o))) {
    return 'TRAFFIC';
  }
  if (['OUTCOME_ENGAGEMENT', 'POST_ENGAGEMENT', 'PAGE_LIKES', 'EVENT_RESPONSES'].some(o => upperObjective.includes(o))) {
    return 'ENGAGEMENT';
  }
  if (['OUTCOME_AWARENESS', 'REACH', 'BRAND_AWARENESS', 'VIDEO_VIEWS'].some(o => upperObjective.includes(o))) {
    return 'AWARENESS';
  }
  if (['OUTCOME_LEADS', 'LEAD_GENERATION', 'MESSAGES', 'MESSENGER'].some(o => upperObjective.includes(o))) {
    return 'LEADS';
  }
  if (['OUTCOME_SALES', 'CONVERSIONS', 'CATALOG_SALES', 'PURCHASE'].some(o => upperObjective.includes(o))) {
    return 'SALES';
  }
  if (['OUTCOME_APP_PROMOTION', 'APP_INSTALLS'].some(o => upperObjective.includes(o))) {
    return 'APP';
  }
  
  return 'SALES';
};

// Check if ROAS should be displayed for this campaign type
const shouldShowROAS = (objective, roas, revenue) => {
  const category = getObjectiveCategory(objective);
  // Only show ROAS for SALES campaigns that have actual revenue data
  if (category !== 'SALES') return false;
  if (!roas || roas <= 0) return false;
  return true;
};

// Get objective display name
const getObjectiveDisplayName = (objective) => {
  const objectiveMap = {
    'OUTCOME_TRAFFIC': 'Traffic',
    'OUTCOME_AWARENESS': 'Awareness',
    'OUTCOME_ENGAGEMENT': 'Engagement',
    'OUTCOME_LEADS': 'Lead Gen',
    'OUTCOME_SALES': 'Conversions',
    'OUTCOME_APP_PROMOTION': 'App Promotion',
    'POST_ENGAGEMENT': 'Engagement',
    'LINK_CLICKS': 'Traffic',
    'REACH': 'Awareness',
    'BRAND_AWARENESS': 'Awareness',
    'LEAD_GENERATION': 'Lead Gen',
    'CONVERSIONS': 'Conversions',
    'APP_INSTALLS': 'App Promotion',
    'VIDEO_VIEWS': 'Video Views',
    'MESSAGES': 'Messages',
    'messages': 'Messages'
  };
  return objectiveMap[objective] || objective?.toLowerCase().replace(/_/g, ' ') || 'conversions';
};

const CampaignDeepAnalysis = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const passedCampaign = location.state?.campaign;
  const passedAnalysisData = location.state?.analysisData;

  const [campaign, setCampaign] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [geminiInsights, setGeminiInsights] = useState(null);
  const [performanceScore, setPerformanceScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [deepAnalysisLoading, setDeepAnalysisLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [timelineVisibleCount, setTimelineVisibleCount] = useState(10);
  const [selectedChartDay, setSelectedChartDay] = useState(null);
  const chatContainerRef = useRef(null);
  const [timelinePage, setTimelinePage] = useState(1);
  const TIMELINE_ITEMS_PER_PAGE = 10;
  const DataRangeBadge = ({ days = "60", type = "reach" }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    {type === "reach" ? `${days}-Day Window` : `Last ${days} Days`}
  </span>
);


  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://whydiditdie.com';

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

useEffect(() => {
  if (geminiInsights) {
    setChatHistory([
      {
        role: 'model',
        text: `I've completed my initial forensic analysis of "${campaign.name}". What specific questions do you have? Feel free to ask about performance trends, recommendations, or how this campaign compares to industry benchmarks.`
      }
    ]);
  }
}, [geminiInsights, campaign?.name]);

  useEffect(() => {
    if (passedCampaign) {
      const campaignWithInitialData = {
        ...passedCampaign,
        wasteAmount: passedCampaign.wasteAmount || passedCampaign.aiDetectedWaste || passedCampaign.totalLoss || 0,
        wastePercentage: passedCampaign.wastePercentage || 0,
        currency: passedCampaign.currency || 'USD'
      };
      setCampaign(campaignWithInitialData);
      setLoading(false);
      fetchDeepAnalysisData(campaignWithInitialData);
    } else if (campaignId) {
      setError(`Campaign data not available. Please navigate from Campaign Detective.`);
      setLoading(false);
    }
  }, [campaignId, passedCampaign]);

  const fetchDeepAnalysisData = async (campaignData) => {
    try {
      setDeepAnalysisLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (!session?.user) {
        logger.error('No authenticated user found');
        return;
      }

      await fetchPerformanceHistory(campaignData, session.user.id);
      await fetchGeminiDeepAnalysis(campaignData, session.user.id, token);
      calculatePerformanceScore(campaignData);
      
    } catch (err) {
      logger.error('Error fetching deep analysis data:', err);
    } finally {
      setDeepAnalysisLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!userMessage.trim() || isChatLoading) return;

    const newMessage = { role: 'user', text: userMessage };
    const updatedHistory = [...chatHistory, newMessage];
    setChatHistory(updatedHistory);
    setUserMessage('');
    setIsChatLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${BACKEND_URL}/api/campaigns/deep-analysis/${campaignId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          history: chatHistory
        })
      });

      if (!response.ok) {
        throw new Error('AI chat response failed.');
      }

      const data = await response.json();
      const aiResponse = { role: 'model', text: data.response };
      setChatHistory([...updatedHistory, aiResponse]);

    } catch (error) {
      logger.error('Chat error:', error);
      const errorResponse = { role: 'model', text: "I'm sorry, but I encountered an error trying to respond. Please try again." };
      setChatHistory([...updatedHistory, errorResponse]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const fetchPerformanceHistory = async (campaignData, userId) => {
    try {
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);
      
      const { data: historyData, error: historyError } = await supabase
        .from('campaign_performance_history')
        .select('*')
        .eq('campaign_id', campaignData.campaign_id)
        .eq('user_id', userId)
        .gte('data_date', sixtyDaysAgo.toISOString().split('T')[0])
        .order('data_date', { ascending: true });

      if (historyError) {
        logger.error('Error fetching performance history:', historyError);
        return;
      }

      logger.info(`[Deep Analysis] Fetched ${historyData?.length || 0} days of performance history`);
      setPerformanceHistory(historyData || []);
      
      if (historyData && historyData.length > 1) {
        generateTimelineFromHistory(historyData, campaignData);
      }
      
    } catch (err) {
      logger.error('Error in fetchPerformanceHistory:', err);
    }
  };

  const generateTimelineFromHistory = (historyData, campaignData) => {
    const timelineEvents = [];
    const objectiveCategory = getObjectiveDisplayName(campaignData.campaign_objective);
    
    for (let i = 1; i < historyData.length; i++) {
      const current = historyData[i];
      const previous = historyData[i - 1];
      
      // Skip if previous values are 0 or null to avoid division errors
      if (previous.ctr && previous.ctr > 0) {
        const ctrChange = ((current.ctr - previous.ctr) / previous.ctr) * 100;
        if (Math.abs(ctrChange) > 15) {
          timelineEvents.push({
            type: ctrChange > 0 ? 'performance_improvement' : 'performance_drop',
            timestamp: current.recorded_at || current.data_date,
            description: `CTR ${ctrChange > 0 ? 'increased' : 'decreased'} by ${Math.abs(ctrChange).toFixed(1)}%`,
            performance_impact: ctrChange,
            old_value: `${previous.ctr.toFixed(2)}%`,
            new_value: `${current.ctr.toFixed(2)}%`,
            metric: 'CTR'
          });
        }
      }
      
      if (current.cpc && previous.cpc && previous.cpc > 0) {
        const cpcChange = ((current.cpc - previous.cpc) / previous.cpc) * 100;
        if (Math.abs(cpcChange) > 20) {
          timelineEvents.push({
            type: cpcChange > 0 ? 'cost_increase' : 'cost_decrease',
            timestamp: current.recorded_at || current.data_date,
            description: `CPC ${cpcChange > 0 ? 'increased' : 'decreased'} by ${Math.abs(cpcChange).toFixed(1)}%`,
            performance_impact: cpcChange > 0 ? -Math.abs(cpcChange) : Math.abs(cpcChange),
            old_value: formatCurrency(previous.cpc, campaignData.currency),
            new_value: formatCurrency(current.cpc, campaignData.currency),
            metric: 'CPC'
          });
        }
      }
      
      if (current.frequency && previous.frequency && previous.frequency > 0) {
        const freqChange = current.frequency - previous.frequency;
        if (freqChange > 0.5) {
          timelineEvents.push({
            type: 'frequency_spike',
            timestamp: current.recorded_at || current.data_date,
            description: `Frequency increased - audience fatigue risk`,
            performance_impact: -freqChange * 10,
            old_value: `${previous.frequency.toFixed(1)}x`,
            new_value: `${current.frequency.toFixed(1)}x`,
            metric: 'Frequency'
          });
        }
      }
      
      if (current.conversion_rate && previous.conversion_rate && previous.conversion_rate > 0) {
        const conversionChange = ((current.conversion_rate - previous.conversion_rate) / previous.conversion_rate) * 100;
        if (Math.abs(conversionChange) > 20) {
          timelineEvents.push({
            type: conversionChange > 0 ? 'conversion_improvement' : 'conversion_decline',
            timestamp: current.recorded_at || current.data_date,
            description: `Conversion rate ${conversionChange > 0 ? 'improved' : 'declined'} by ${Math.abs(conversionChange).toFixed(1)}%`,
            performance_impact: conversionChange,
            old_value: `${previous.conversion_rate.toFixed(2)}%`,
            new_value: `${current.conversion_rate.toFixed(2)}%`,
            metric: 'Conversion Rate'
          });
        }
      }
    }
    
    timelineEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setTimeline(timelineEvents);
  };

  const fetchGeminiDeepAnalysis = async (campaignData, userId, token) => {
    try {
      const enhancedPayload = {
        userId: userId,
        campaign: {
          ...campaignData,
          existingWasteAmount: campaignData.wasteAmount || 0,
          existingWastePercentage: campaignData.wastePercentage || 0
        },
        performanceHistory: performanceHistory,
        analysisType: 'deep_forensic'
      };
      
      const analysisResponse = await fetch(`${BACKEND_URL}/api/gemini-analysis/deep-campaign-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(enhancedPayload)
      });

      if (!analysisResponse.ok) {
        throw new Error(`Gemini analysis failed: ${analysisResponse.status}`);
      }

      const analysisData = await analysisResponse.json();
      
      logger.info('='.repeat(80));
      logger.info('🔍 GEMINI API ENDPOINT RESPONSE');
      logger.info('='.repeat(80));
      logger.info('Response structure:', {
        keys: Object.keys(analysisData),
        hasCampaign: !!analysisData.campaign,
        hasMetadata: !!analysisData.metadata,
        hasAnalysis: !!analysisData.analysis
      });
      
      if (analysisData.campaign) {
        logger.info('📦 analysisData.campaign:', {
          conversions: analysisData.campaign.conversions,
          messaging_conversations: analysisData.campaign.messaging_conversations,
          leads: analysisData.campaign.leads,
          purchases: analysisData.campaign.purchases
        });
      }
      
      if (analysisData.metadata) {
        logger.info('📊 analysisData.metadata:', {
          smartConversionsDetected: analysisData.metadata.smartConversionsDetected,
          conversionTypeDetected: analysisData.metadata.conversionTypeDetected
        });
      }
      logger.info('='.repeat(80));
      
      setGeminiInsights(analysisData);
      
      // Extract conversions from multiple possible locations in the response
      const backendCampaign = analysisData.campaign || analysisData.analysis?.campaign || {};
      const metadata = analysisData.metadata || {};
      
      logger.info('[Deep Analysis Frontend] Extracted backend campaign:', {
        hasConversions: !!backendCampaign.conversions,
        conversions: backendCampaign.conversions,
        messaging_conversations: backendCampaign.messaging_conversations,
        leads: backendCampaign.leads,
        purchases: backendCampaign.purchases,
        metadataSmartConversions: metadata.smartConversionsDetected,
        metadataConversionType: metadata.conversionTypeDetected
      });
      
      let extractedWasteAmount = analysisData.wasteAmount;
      let extractedWastePercentage = analysisData.wastePercentage;
      
      if ((extractedWasteAmount === undefined || extractedWasteAmount === 0) && analysisData.wasteExplanation) {
        const dollarMatches = analysisData.wasteExplanation.match(/\$([0-9,]+(?:\.\d{2})?)/g);
        if (dollarMatches && dollarMatches.length > 0) {
          const amounts = dollarMatches.map(m => parseFloat(m.replace(/[\$,]/g, '')));
          extractedWasteAmount = Math.max(...amounts);
        }
        
        const percentMatch = analysisData.wasteExplanation.match(/(\d+(?:\.\d+)?)\s*%/);
        if (percentMatch) {
          extractedWastePercentage = parseFloat(percentMatch[1]);
        }
      }
      
      // Sanity check: waste amount should never exceed spend
      const maxWaste = campaignData.spend || 0;
      if (extractedWasteAmount > maxWaste) {
        extractedWasteAmount = maxWaste * (extractedWastePercentage / 100) || 0;
      }
      
      // CRITICAL FIX: Extract conversions from backend response
      // Check multiple possible locations in the response structure
      // (backendCampaign and metadata already extracted above)
      
      // Priority 1: Try metadata first (from our smart detection)
      let updatedConversions = metadata.smartConversionsDetected || 0;
      let conversionSource = 'metadata';
      
      // Priority 2: Try backend campaign object
      if (updatedConversions === 0 && backendCampaign.conversions > 0) {
        updatedConversions = backendCampaign.conversions;
        conversionSource = 'backendCampaign.conversions';
      }
      
      // Priority 3: Try direct analysisData
      if (updatedConversions === 0 && analysisData.conversions > 0) {
        updatedConversions = analysisData.conversions;
        conversionSource = 'analysisData.conversions';
      }
      
      // Priority 4: Try campaign data passed in
      if (updatedConversions === 0) {
        updatedConversions = campaignData.conversions || 0;
        conversionSource = 'campaignData.conversions';
      }
      
      let updatedConversionRate = backendCampaign.conversion_rate || campaignData.conversion_rate || 0;
      let updatedMessagingConversations = backendCampaign.messaging_conversations || campaignData.messaging_conversations || 0;
      let updatedLeads = backendCampaign.leads || campaignData.leads || 0;
      let updatedPurchases = backendCampaign.purchases || campaignData.purchases || 0;
      let updatedLinkClicks = backendCampaign.link_clicks || campaignData.link_clicks || 0;
      let updatedAppInstalls = backendCampaign.app_installs || campaignData.app_installs || 0;
      
      // 🔥 RECALCULATE CONVERSION RATE based on updated conversions
      // Conversion Rate = (Conversions / Clicks) * 100
      const clicks = campaignData.clicks || backendCampaign.clicks || 0;
      if (clicks > 0 && updatedConversions > 0) {
        updatedConversionRate = (updatedConversions / clicks) * 100;
        logger.info(`[Deep Analysis] Recalculated conversion rate: ${updatedConversions} conversions ÷ ${clicks} clicks = ${updatedConversionRate.toFixed(2)}%`);
      } else if (updatedConversions === 0) {
        updatedConversionRate = 0;
      }
      
      logger.info('[Deep Analysis Frontend] Conversion extraction results:', {
        conversions: updatedConversions,
        conversion_rate: updatedConversionRate,
        clicks: clicks,
        source: conversionSource,
        messaging_conversations: updatedMessagingConversations,
        leads: updatedLeads,
        purchases: updatedPurchases,
        link_clicks: updatedLinkClicks,
        app_installs: updatedAppInstalls
      });
      
      const objective = campaignData.campaign_objective?.toUpperCase() || '';
      const isMessagesObjective = objective.includes('MESSAGES') || objective.includes('MESSENGER');
      const isLeadsObjective = objective.includes('LEAD');
      const isSalesObjective = objective.includes('SALES') || objective.includes('PURCHASE') || objective.includes('CONVERSIONS');
      
      // FALLBACK: Check if AI detected conversions in text when backend didn't return them
      if (updatedConversions === 0 && updatedMessagingConversations === 0 && (analysisData.insights || analysisData.diagnosis)) {
        const textToSearch = [
          analysisData.diagnosis || '',
          ...(analysisData.insights || []).map(i => i.message || '')
        ].join(' ');
        
        // Extract messaging conversations (for Messages campaigns)
        if (isMessagesObjective) {
          const conversationsMatch = textToSearch.match(/(\d+)\s*conversation/i);
          if (conversationsMatch) {
            updatedMessagingConversations = parseInt(conversationsMatch[1]);
            updatedConversions = updatedMessagingConversations;
            logger.info(`[Deep Analysis] Extracted ${updatedMessagingConversations} conversations from AI text`);
          }
        }
        
        // Extract leads (for Lead Gen campaigns)
        if (isLeadsObjective) {
          const leadsMatch = textToSearch.match(/(\d+)\s*lead/i);
          if (leadsMatch) {
            updatedLeads = parseInt(leadsMatch[1]);
            updatedConversions = updatedLeads;
            logger.info(`[Deep Analysis] Extracted ${updatedLeads} leads from AI text`);
          }
        }
        
        // Extract purchases (for Sales campaigns)
        if (isSalesObjective) {
          const purchasesMatch = textToSearch.match(/(\d+)\s*purchase/i);
          if (purchasesMatch) {
            updatedPurchases = parseInt(purchasesMatch[1]);
            updatedConversions = updatedPurchases;
            logger.info(`[Deep Analysis] Extracted ${updatedPurchases} purchases from AI text`);
          }
        }
        
        // Fallback: Look for any "conversion" mentions
        if (updatedConversions === 0) {
          const conversionMatch = textToSearch.match(/(\d+)\s*conversion/i);
          if (conversionMatch) {
            updatedConversions = parseInt(conversionMatch[1]);
            logger.info(`[Deep Analysis] Extracted ${updatedConversions} conversions from AI text`);
          }
        }
      }
      
      // Recalculate conversion rate if we found conversions
      if (updatedConversions > 0 && campaignData.clicks > 0 && updatedConversionRate === 0) {
        updatedConversionRate = (updatedConversions / campaignData.clicks) * 100;
      }
      
      setCampaign(prevCampaign => ({
        ...prevCampaign,
        diagnosis: analysisData.diagnosis || generateDiagnosis(prevCampaign),
        recommendation: analysisData.recommendation || generateRecommendation(prevCampaign),
        wasteExplanation: analysisData.wasteExplanation || generateWasteExplanation(prevCampaign),
        wasteAmount: extractedWasteAmount !== undefined ? extractedWasteAmount : prevCampaign.wasteAmount || 0,
        wastePercentage: extractedWastePercentage !== undefined ? extractedWastePercentage : prevCampaign.wastePercentage || 0,
        severity: analysisData.severity || prevCampaign.severity || 'MONITORING',
        campaignSpecificInsights: analysisData.insights || [],
        timelineInsights: analysisData.timelineInsights || [],
        recommendations: analysisData.recommendations || [],
        // Update all conversion types from backend response
        conversions: updatedConversions,
        conversion_rate: updatedConversionRate,
        messaging_conversations: updatedMessagingConversations,
        leads: updatedLeads,
        purchases: updatedPurchases,
        link_clicks: updatedLinkClicks,
        app_installs: updatedAppInstalls
      }));
      
      logger.info('[Deep Analysis Frontend] Updated campaign state with:', {
        conversions: updatedConversions,
        messaging_conversations: updatedMessagingConversations,
        leads: updatedLeads
      });
      
      // Recalculate performance score with updated conversions
      if (updatedConversions > 0 && campaignData.conversions === 0) {
        calculatePerformanceScore({
          ...campaignData,
          conversions: updatedConversions,
          conversion_rate: updatedConversionRate
        });
      }
      
    } catch (err) {
      logger.error('Gemini deep analysis failed:', err);
      
      setCampaign(prevCampaign => ({
        ...prevCampaign,
        diagnosis: generateDiagnosis(prevCampaign),
        recommendation: generateRecommendation(prevCampaign),
        wasteExplanation: generateWasteExplanation(prevCampaign),
        campaignSpecificInsights: [],
        timelineInsights: []
      }));
    }
  };

  const generateDiagnosis = (campaign) => {
    const objectiveCategory = getObjectiveDisplayName(campaign.campaign_objective);
    const status = campaign.severity || 'MONITORING';
    
    if (status === 'CRITICAL') {
      return `Critical performance issues detected in this ${objectiveCategory} campaign. CTR of ${campaign.ctr?.toFixed(2) || 0}% is significantly below benchmarks, indicating poor audience targeting or creative fatigue.`;
    } else if (status === 'CHAMPION') {
      return `Exceptional ${objectiveCategory} campaign performance. CTR of ${campaign.ctr?.toFixed(2) || 0}% exceeds benchmarks by a significant margin. This campaign is ready for scaling.`;
    }
    
    return `${objectiveCategory} campaign showing moderate performance. Current metrics indicate room for optimization in targeting and creative strategy.`;
  };

  const generateRecommendation = (campaign) => {
    const status = campaign.severity || 'MONITORING';
    
    if (status === 'CRITICAL') {
      return `Immediate action required: 1) Pause campaign and audit targeting, 2) Refresh all creative assets, 3) Review landing page experience, 4) Consider switching to manual bidding for better control.`;
    } else if (status === 'CHAMPION') {
      return `Scale this winning campaign: 1) Increase budget by 50-100%, 2) Expand to similar audiences, 3) Test new geographic markets, 4) Maintain creative freshness with regular updates.`;
    }
    
    return `Optimize for better results: 1) A/B test new creative variations, 2) Refine audience targeting, 3) Test different bid strategies, 4) Monitor frequency to prevent fatigue.`;
  };

  const generateWasteExplanation = (campaign) => {
    const wasteAmount = campaign.wasteAmount || 0;
    const wastePercentage = campaign.wastePercentage || 0;
    
    if (wasteAmount === 0 || wasteAmount < 1) {
      return `No significant waste detected. Campaign is performing efficiently within acceptable parameters.`;
    }
    
    return `Detected potential waste of ${formatCurrency(wasteAmount, campaign.currency)} primarily due to ${
      campaign.ctr < 1.0 ? 'low click-through rate' : 
      campaign.frequency > 3 ? 'high frequency causing audience fatigue' :
      campaign.conversion_rate < 2 ? 'poor conversion performance' :
      'suboptimal targeting efficiency'
    }. This represents opportunity for optimization.`;
  };

  const calculatePerformanceScore = (campaignData) => {
    const { ctr, cpc, conversion_rate, frequency } = campaignData;
    
    let totalScore = 0;
    let scoreCount = 0;
    
    if (ctr) {
      const ctrScore = Math.min(100, (ctr / 1.5) * 100);
      totalScore += ctrScore;
      scoreCount++;
    }
    
    if (cpc) {
      const cpcScore = Math.min(100, (2.0 / cpc) * 100);
      totalScore += cpcScore;
      scoreCount++;
    }
    
    if (conversion_rate) {
      const conversionScore = Math.min(100, (conversion_rate / 3.0) * 100);
      totalScore += conversionScore * 1.5;
      scoreCount += 1.5;
    }
    
    if (frequency && frequency > 0) {
      const frequencyScore = frequency <= 2.5 ? 100 : Math.max(20, 100 - ((frequency - 2.5) * 20));
      totalScore += frequencyScore * 0.8;
      scoreCount += 0.8;
    }
    
    const overallScore = scoreCount > 0 ? Math.round(totalScore / scoreCount) : 50;
    
    setPerformanceScore({
      overall: Math.max(0, Math.min(100, overallScore)),
      ctr: ctr ? Math.round(Math.min(100, (ctr / 1.5) * 100)) : 50,
      cpc: cpc ? Math.round(Math.min(100, (2.0 / cpc) * 100)) : 50,
      conversion: conversion_rate ? Math.round(Math.min(100, (conversion_rate / 3.0) * 100)) : 50,
      quality: Math.round(overallScore * 0.9 + 10)
    });
  };

  const getEventIcon = (type) => {
    const icons = {
      'performance_improvement': '📈',
      'performance_drop': '📉',
      'frequency_spike': '🔄',
      'cost_increase': '💸',
      'cost_decrease': '💰',
      'conversion_improvement': '🎯',
      'conversion_decline': '📉'
    };
    return icons[type] || '📊';
  };

  const getEventColor = (type, impact) => {
    if (impact > 10) return 'border-l-4 border-green-500 bg-green-500/10';
    if (impact > 0) return 'border-l-4 border-yellow-500 bg-yellow-500/10';
    if (impact < -20) return 'border-l-4 border-red-500 bg-red-500/10';
    if (impact < 0) return 'border-l-4 border-orange-500 bg-orange-500/10';
    return 'border-l-4 border-gray-500 bg-gray-500/10';
  };

  const getImpactColor = (impact) => {
    if (impact > 10) return 'text-green-500';
    if (impact > 0) return 'text-yellow-500';
    if (impact < -20) return 'text-red-500';
    if (impact < 0) return 'text-orange-500';
    return 'text-gray-500';
  };

  const getPerformanceScoreColor = (score) => {
    if (score >= 90) return 'text-green-500';
    if (score >= 70) return 'text-yellow-500';
    if (score >= 50) return 'text-orange-500';
    return 'text-red-500';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'CHAMPION': 'text-green-500',
      'HEALTHY': 'text-green-400',
      'MONITORING': 'text-yellow-500',
      'CRITICAL': 'text-orange-500',
      'COMPLETED': 'text-purple-500',
      'PAUSED': 'text-gray-500'
    };
    return colors[severity] || 'text-gray-500';
  };

  const isActiveCampaign = () => {
    return campaign?.actual_status === 'active' || campaign?.effective_status === 'ACTIVE';
  };

  // Get conversions based on objective type
  // Get conversions based on objective type
  // PRIORITY: Check what data actually exists, not just the objective name
  const getConversions = (campaignData) => {
    const category = getObjectiveCategory(campaignData?.campaign_objective);
    const objective = campaignData?.campaign_objective?.toUpperCase() || '';
    
    // SMART DETECTION: Check if messaging_conversations exists first
    // This handles cases where FB API returns wrong objective but has conversation data
    if (campaignData?.messaging_conversations && campaignData.messaging_conversations > 0) {
      return campaignData.messaging_conversations;
    }
    
    // Check for Messages campaigns by objective name
    if (objective.includes('MESSAGES') || objective.includes('MESSENGER')) {
      return campaignData?.success_metric_value || campaignData?.conversions || 0;
    }
    
    // SMART DETECTION: Check if leads exists
    if (campaignData?.leads && campaignData.leads > 0) {
      return campaignData.leads;
    }
    
    // SMART DETECTION: Check if purchases exists
    if (campaignData?.purchases && campaignData.purchases > 0) {
      return campaignData.purchases;
    }
    
    // SMART DETECTION: Check if app_installs exists
    if (campaignData?.app_installs && campaignData.app_installs > 0) {
      return campaignData.app_installs;
    }
    
    // Fallback to category-based detection
    switch(category) {
      case 'TRAFFIC':
        // Traffic campaigns show clicks separately, conversions are website actions
        return campaignData?.conversions || 0;
      case 'LEADS':
        return campaignData?.leads || campaignData?.conversions || 0;
      case 'SALES':
        return campaignData?.purchases || campaignData?.conversions || 0;
      case 'APP':
        return campaignData?.app_installs || campaignData?.conversions || 0;
      default:
        return campaignData?.conversions || 0;
    }
  };

  // Get cost per conversion
  const getCostPerConversion = (campaignData) => {
    const conversions = getConversions(campaignData);
    if (!conversions || conversions === 0) return null;
    return (campaignData?.spend || 0) / conversions;
  };

  // Handle chart touch/click for mobile
  const handleChartInteraction = (day, index) => {
    setSelectedChartDay(selectedChartDay === index ? null : index);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4 animate-pulse">🔬</div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text mb-2">
            Deep Forensic Analysis in Progress...
          </h2>
          <p className="text-gray-400">
            Analyzing campaign performance with AI intelligence
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🚨</div>
          <p className="text-red-500 text-xl font-bold mb-4">{error}</p>
          <button 
            onClick={() => navigate('/campaign-detective')} 
            className="bg-gradient-to-r from-gray-700 to-gray-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
          >
            ← Back to Campaign Detective
          </button>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <p className="text-red-500 text-xl font-bold mb-4">Campaign not found</p>
          <button 
            onClick={() => navigate('/campaign-detective')} 
            className="bg-gradient-to-r from-gray-700 to-gray-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
          >
            ← Back to Campaign Detective
          </button>
        </div>
      </div>
    );
  }

  const objectiveCategory = getObjectiveDisplayName(campaign.campaign_objective);
  const objectiveCategoryKey = getObjectiveCategory(campaign.campaign_objective);
  const conversions = getConversions(campaign);
  const costPerConversion = getCostPerConversion(campaign);
  const displayWasteAmount = campaign.wasteAmount && campaign.wasteAmount > 0 ? campaign.wasteAmount : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="p-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/campaign-detective')} 
            className="bg-gradient-to-r from-gray-700 to-gray-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all mb-4"
          >
            ← Back to Campaign Detective
          </button>
          
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            <div className="text-5xl">🔬</div>
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-black bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text">
                DEEP FORENSIC ANALYSIS
              </h1>
              <p className="text-xl text-gray-300 mt-1">{campaign.name}</p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-400 mt-2">
                <span className="text-blue-400">{campaign.platform}</span>
                <span>Status: <span className={
                  campaign.actual_status === 'active' || campaign.effective_status === 'ACTIVE' ? 'text-green-400' : 
                  campaign.actual_status === 'paused' ? 'text-yellow-400' : 
                  campaign.actual_status === 'completed' ? 'text-purple-400' : 'text-gray-400'
                }>
                  {campaign.effective_status || campaign.actual_status?.charAt(0).toUpperCase() + campaign.actual_status?.slice(1)}
                </span></span>
                <span className="text-green-400">
                  Objective: {objectiveCategory}
                </span>
                <span className="text-green-400">AI Confidence: {geminiInsights?.confidence || 96}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Deep Analysis Loading */}
        {deepAnalysisLoading && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-8 flex items-center gap-2 text-green-500">
            <span className="text-xl animate-pulse">🤖</span>
            <span>Performing deep AI analysis with Aura intelligence...</span>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 border-b border-gray-700 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 lg:px-6 py-3 text-sm font-bold transition-all whitespace-nowrap ${
              activeTab === 'overview' 
                ? 'text-green-400 border-b-2 border-green-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            🎯 AI Overview
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-4 lg:px-6 py-3 text-sm font-bold transition-all whitespace-nowrap ${
              activeTab === 'timeline' 
                ? 'text-green-400 border-b-2 border-green-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            📅 Performance Timeline
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`px-4 lg:px-6 py-3 text-sm font-bold transition-all whitespace-nowrap ${
              activeTab === 'performance' 
                ? 'text-green-400 border-b-2 border-green-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            📊 Performance Deep Dive
          </button>
          {isActiveCampaign() && (
            <button
              onClick={() => setActiveTab('recommendations')}
              className={`px-4 lg:px-6 py-3 text-sm font-bold transition-all whitespace-nowrap ${
                activeTab === 'recommendations' 
                  ? 'text-green-400 border-b-2 border-green-400' 
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              🤖 AI Recommendations
            </button>
          )}
          <button
            onClick={() => setActiveTab('chat')}
            className={`px-4 lg:px-6 py-3 text-sm font-bold transition-all whitespace-nowrap ${
              activeTab === 'chat' 
                ? 'text-green-400 border-b-2 border-green-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            🤖 Chat With Aura
          </button>
        </div>

        {/* Tab Content */}
        <div className="animate-fadeIn">
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* AI Executive Summary */}
              <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
                <h2 className="text-xl font-bold text-green-400 mb-6">🤖 Enhanced AI Executive Summary</h2>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className={`bg-black/50 rounded-lg p-4 text-center border-2 ${getPerformanceScoreColor(performanceScore?.overall || 85).replace('text-', 'border-')}`}>
                    <div className="text-gray-400 text-sm mb-1">Performance Score</div>
                    <div className={`text-2xl lg:text-3xl font-bold ${getPerformanceScoreColor(performanceScore?.overall || 85)}`}>
                      {performanceScore?.overall || '85'}/100
                    </div>
                  </div>
                  
                  <div className={`bg-black/50 rounded-lg p-4 text-center border-2 ${getSeverityColor(campaign.severity).replace('text-', 'border-')}`}>
                    <div className="text-gray-400 text-sm mb-1">Status</div>
                    <div className={`text-2xl lg:text-3xl font-bold ${getSeverityColor(campaign.severity)}`}>
                      {campaign.severity || 'MONITORING'}
                    </div>
                  </div>
                  
                  <div className={`bg-black/50 rounded-lg p-4 text-center border-2 ${displayWasteAmount > 0 ? 'border-red-500' : 'border-green-500'}`}>
                    <div className="text-gray-400 text-sm mb-1">Estimated Waste</div>
                    <div className={`text-2xl lg:text-3xl font-bold ${displayWasteAmount > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {displayWasteAmount > 0 ? formatCurrency(displayWasteAmount, campaign.currency) : '$0'}
                    </div>
                  </div>
                  
                  <div className="bg-black/50 rounded-lg p-4 text-center border-2 border-gray-600">
                    <div className="text-gray-400 text-sm mb-1">Total Spend</div>
                    <div className="text-2xl lg:text-3xl font-bold text-white">
                      {formatCurrency(campaign.spend || 0, campaign.currency)}
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 space-y-4">
                  <div>
                    <h3 className="text-green-400 font-bold mb-2">🩺 Enhanced AI Diagnosis</h3>
                    <p className="text-gray-300">
                      {campaign.diagnosis || generateDiagnosis(campaign)}
                    </p>
                  </div>
                  
                  {campaign.wasteExplanation && displayWasteAmount > 0 && (
                    <div>
                      <h3 className="text-red-400 font-bold mb-2">💸 Objective-Aware Waste Analysis</h3>
                      <p className="text-red-300">
                        {campaign.wasteExplanation}
                      </p>
                    </div>
                  )}
                  
                  {displayWasteAmount === 0 && (
                    <div>
                      <h3 className="text-green-400 font-bold mb-2">✅ Waste Analysis</h3>
                      <p className="text-green-300">
                        No significant waste detected. Campaign is performing efficiently within acceptable parameters.
                      </p>
                    </div>
                  )}
                  
                  {isActiveCampaign() && (
                    <div>
                      <h3 className="text-blue-400 font-bold mb-2">💡 Smart Recommendation</h3>
                      <p className="text-blue-300">
                        {campaign.recommendation || generateRecommendation(campaign)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Campaign-Specific AI Insights */}
              {campaign.campaignSpecificInsights && campaign.campaignSpecificInsights.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-green-400 mb-4">🤖 Campaign Intelligence</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {campaign.campaignSpecificInsights.map((insight, index) => (
                      <div key={index} className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <h4 className="text-blue-400 font-bold mb-2">
                          {insight.type === 'performance' ? '📊' : 
                           insight.type === 'audience' ? '👥' : 
                           insight.type === 'creative' ? '🎨' : '⚡'} 
                          {insight.title}
                        </h4>
                        <p className="text-gray-300 text-sm mb-2">{insight.message}</p>
                        {insight.recommendation && (
                          <p className="text-blue-400 text-sm italic">
                            💡 {insight.recommendation}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Performance Score Breakdown - RESTORED */}
              {performanceScore && (
                <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
                  <h2 className="text-xl font-bold text-green-400 mb-6">📊 Performance Score Breakdown</h2>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-400">CTR Performance</span>
                        <span className={`font-bold ${getPerformanceScoreColor(performanceScore.ctr)}`}>
                          {performanceScore.ctr}/100
                        </span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-1000 ${
                            performanceScore.ctr >= 90 ? 'bg-green-500' :
                            performanceScore.ctr >= 70 ? 'bg-yellow-500' :
                            performanceScore.ctr >= 50 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${performanceScore.ctr}%` }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-400">Cost Efficiency</span>
                        <span className={`font-bold ${getPerformanceScoreColor(performanceScore.cpc)}`}>
                          {performanceScore.cpc}/100
                        </span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-1000 ${
                            performanceScore.cpc >= 90 ? 'bg-green-500' :
                            performanceScore.cpc >= 70 ? 'bg-yellow-500' :
                            performanceScore.cpc >= 50 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${performanceScore.cpc}%` }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-400">Conversion Performance</span>
                        <span className={`font-bold ${getPerformanceScoreColor(performanceScore.conversion)}`}>
                          {performanceScore.conversion}/100
                        </span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-1000 ${
                            performanceScore.conversion >= 90 ? 'bg-green-500' :
                            performanceScore.conversion >= 70 ? 'bg-yellow-500' :
                            performanceScore.conversion >= 50 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${performanceScore.conversion}%` }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-400">Quality Score</span>
                        <span className={`font-bold ${getPerformanceScoreColor(performanceScore.quality)}`}>
                          {performanceScore.quality}/100
                        </span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all duration-1000 ${
                            performanceScore.quality >= 90 ? 'bg-green-500' :
                            performanceScore.quality >= 70 ? 'bg-yellow-500' :
                            performanceScore.quality >= 50 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${performanceScore.quality}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-gray-800/50 p-4 rounded-lg text-center mt-6">
                <p className="text-gray-400 text-sm">
                  These scores are based on our system benchmarks. For a more personalized report, 
                  <a href="/settings/performance-goals" className="text-green-400 hover:underline ml-1 font-semibold">
                    set your own performance goals.
                  </a>
                </p>
              </div>

              {/* Key Metrics - LIFETIME from snapshot - 8 boxes for ALL campaign types */}
              <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
                <h2 className="text-xl font-bold text-green-400 mb-6">
                  📊 {objectiveCategory} Lifetime Performance Metrics
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Row 1: CTR, CPC, Clicks, Impressions - Always show */}
                  <div className="bg-black/50 rounded-lg p-4 text-center">
                    <div className="text-xl lg:text-2xl font-bold text-white">{formatPercent(campaign.ctr)}</div>
                    <div className="text-gray-400 text-sm">CTR</div>
                  </div>
                  <div className="bg-black/50 rounded-lg p-4 text-center">
                    <div className="text-xl lg:text-2xl font-bold text-white">{formatCurrency(campaign.cpc || 0, campaign.currency)}</div>
                    <div className="text-gray-400 text-sm">CPC</div>
                  </div>
                  <div className="bg-black/50 rounded-lg p-4 text-center">
                    <div className="text-xl lg:text-2xl font-bold text-white">{campaign.clicks?.toLocaleString() || '0'}</div>
                    <div className="text-gray-400 text-sm">Clicks</div>
                  </div>
                  <div className="bg-black/50 rounded-lg p-4 text-center">
                    <div className="text-xl lg:text-2xl font-bold text-white">{campaign.impressions?.toLocaleString() || '0'}</div>
                    <div className="text-gray-400 text-sm">Impressions</div>
                  </div>
                  
                  {/* Row 2: Conversions, Conv. Rate, Cost/Conv, and either Reach or ROAS */}
                  <div className="bg-black/50 rounded-lg p-4 text-center">
                    <div className="text-xl lg:text-2xl font-bold text-white">{conversions?.toLocaleString() || '0'}</div>
                    <div className="text-gray-400 text-sm">
                      {objectiveCategoryKey === 'LEADS' ? 'Leads' : 
                       objectiveCategoryKey === 'APP' ? 'Installs' : 'Conversions'}
                    </div>
                  </div>
                  <div className="bg-black/50 rounded-lg p-4 text-center">
                    <div className="text-xl lg:text-2xl font-bold text-white">{formatPercent(campaign.conversion_rate)}</div>
                    <div className="text-gray-400 text-sm">Conv. Rate</div>
                  </div>
                  <div className="bg-black/50 rounded-lg p-4 text-center">
                    <div className="text-xl lg:text-2xl font-bold text-white">
                      {costPerConversion ? formatCurrency(costPerConversion, campaign.currency) : 'N/A'}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {objectiveCategoryKey === 'LEADS' ? 'Cost/Lead' : 
                       objectiveCategoryKey === 'APP' ? 'Cost/Install' : 'Cost/Conv'}
                    </div>
                  </div>
                  
                  {/* Show ROAS only for SALES campaigns with actual ROAS data */}
                  {shouldShowROAS(campaign.campaign_objective, campaign.roas, campaign.revenue) ? (
                    <div className="bg-black/50 rounded-lg p-4 text-center">
                      <div className="text-xl lg:text-2xl font-bold text-white">{safeNumber(campaign.roas, 2) || '0'}x</div>
                      <div className="text-gray-400 text-sm">ROAS</div>
                    </div>
                  ) : (
                    <div className="bg-black/50 rounded-lg p-4 text-center">
                      <div className="text-xl lg:text-2xl font-bold text-white">{campaign.reach?.toLocaleString() || '0'}</div>
                      <div className="text-gray-400 text-sm">Reach</div>
                    </div>
                  )}
                </div>
                
                {/* Additional row for Frequency if present */}
                {campaign.frequency !== null && campaign.frequency !== undefined && campaign.frequency > 0 && (
                  <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-black/50 rounded-lg p-4 text-center">
                      <div className="text-xl lg:text-2xl font-bold text-white">{formatFrequency(campaign.frequency)}</div>
                      <div className="text-gray-400 text-sm">Frequency</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        {activeTab === 'timeline' && (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-green-400 mb-2">📅 Campaign Performance Timeline</h2>
      <p className="text-gray-400 mb-6">
        Performance changes and events that affected this campaign
      </p>
    </div>
    
    {timeline.length === 0 ? (
      <div className="bg-gray-900/50 rounded-xl p-12 text-center border border-gray-700">
        <div className="text-6xl mb-4">📊</div>
        <p className="text-yellow-500 text-xl font-bold mb-2">
          {performanceHistory.length === 0 ? 'No Performance History Available' : 'No Significant Changes Detected'}
        </p>
        <p className="text-gray-400">
          {performanceHistory.length === 0 
            ? 'This campaign needs more performance data to generate timeline insights. Daily data will populate within 24-48 hours.'
            : 'Campaign performance has been stable with no significant changes detected in the monitored period.'
          }
        </p>
      </div>
    ) : (
      <>
        {/* Stats Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-400">{timeline.length}</div>
            <div className="text-gray-400 text-sm">Events Detected</div>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-400">{performanceHistory.length}</div>
            <div className="text-gray-400 text-sm">Days Analyzed</div>
          </div>
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-400">
              {timeline.filter(e => e.performance_impact > 0).length}
            </div>
            <div className="text-gray-400 text-sm">Positive Changes</div>
          </div>
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-400">{objectiveCategory}</div>
            <div className="text-gray-400 text-sm">Objective Focus</div>
          </div>
        </div>
        
        {/* Timeline Events */}
        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-700 hidden lg:block"></div>
          
          {timeline
            .slice((timelinePage - 1) * TIMELINE_ITEMS_PER_PAGE, timelinePage * TIMELINE_ITEMS_PER_PAGE)
            .map((event, index) => (
              <div key={index} className="relative mb-6">
                <div className="absolute left-8 w-4 h-4 bg-green-500 rounded-full -translate-x-1/2 border-2 border-black hidden lg:block"></div>
                
                <div className={`lg:ml-16 p-4 lg:p-6 rounded-lg ${getEventColor(event.type, event.performance_impact)}`}>
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getEventIcon(event.type)}</span>
                      <div>
                        <h3 className="text-yellow-400 font-bold text-lg">
                          {event.metric} Change
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {new Date(event.timestamp).toLocaleDateString('en-US', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="bg-black/50 rounded-lg px-3 py-1">
                      <span className={`font-bold ${getImpactColor(event.performance_impact)}`}>
                        {event.performance_impact > 0 ? '+' : ''}{event.performance_impact.toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  <p className="text-gray-300 mb-3">{event.description}</p>
                  
                  {event.old_value && event.new_value && (
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-red-400">From: {event.old_value}</span>
                      <span className="text-gray-500">→</span>
                      <span className="text-green-400">To: {event.new_value}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
        
        {/* Numbered Pagination */}
        {timeline.length > TIMELINE_ITEMS_PER_PAGE && (
          <div className="flex justify-center items-center gap-2 mt-6 flex-wrap">
            {/* Previous Button */}
            <button
              onClick={() => setTimelinePage(prev => Math.max(1, prev - 1))}
              disabled={timelinePage === 1}
              className="px-3 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-all"
            >
              ←
            </button>
            
            {/* Page Numbers */}
            {(() => {
              const totalPages = Math.ceil(timeline.length / TIMELINE_ITEMS_PER_PAGE);
              const pages = [];
              
              for (let i = 1; i <= totalPages; i++) {
                // Show first page, last page, current page, and pages around current
                if (
                  i === 1 || 
                  i === totalPages || 
                  (i >= timelinePage - 1 && i <= timelinePage + 1)
                ) {
                  pages.push(i);
                } else if (pages[pages.length - 1] !== '...') {
                  pages.push('...');
                }
              }
              
              return pages.map((page, idx) => (
                page === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">...</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setTimelinePage(page)}
                    className={`min-w-[40px] px-3 py-2 rounded-lg font-bold transition-all ${
                      timelinePage === page
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {page}
                  </button>
                )
              ));
            })()}
            
            {/* Next Button */}
            <button
              onClick={() => setTimelinePage(prev => Math.min(Math.ceil(timeline.length / TIMELINE_ITEMS_PER_PAGE), prev + 1))}
              disabled={timelinePage >= Math.ceil(timeline.length / TIMELINE_ITEMS_PER_PAGE)}
              className="px-3 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-all"
            >
              →
            </button>
            
            {/* Page Info */}
            <span className="ml-4 text-gray-400 text-sm">
              Page {timelinePage} of {Math.ceil(timeline.length / TIMELINE_ITEMS_PER_PAGE)}
            </span>
          </div>
        )}
      </>
    )}
  </div>
)}

        {activeTab === 'performance' && (
  <div className="space-y-8">
    <div>
      <h2 className="text-2xl font-bold text-green-400 mb-2">📊 Performance Deep Dive</h2>
      <p className="text-gray-400 mb-6">
        Detailed performance analysis for your {objectiveCategory} campaign
      </p>
    </div>

    {/* AI Performance Insights - Synced with Overview */}
<div>
  <h3 className="text-xl font-bold text-green-400 mb-4">🤖 Aura's Performance Insights</h3>
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    
    {/* Targeting Analysis - Objective Aware */}
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
      <h4 className="text-blue-400 font-bold mb-2">🎯 {objectiveCategory} Performance</h4>
      <p className="text-gray-300 text-sm">
        {(() => {
          if (objectiveCategoryKey === 'MESSAGES') {
            const convos = campaign.messaging_conversations || campaign.success_metric_value || 0;
            if (convos > 20 && campaign.ctr > 5) {
              return `Crushing it! 🔥 ${convos} conversations with ${campaign.ctr?.toFixed(1)}% CTR shows your creative is resonating. People want to chat!`;
            } else if (convos > 0) {
              return `${convos} conversations started. For a Messages campaign, we're looking at conversation volume and cost - not website conversions!`;
            }
            return `This is a Messages campaign optimized for conversations. The strong ${campaign.ctr?.toFixed(1)}% CTR shows interest - conversations may take time to track.`;
          } else if (objectiveCategoryKey === 'TRAFFIC') {
            const clicks = campaign.link_clicks || campaign.clicks || 0;
            if (campaign.ctr > 5) {
              return `That ${campaign.ctr?.toFixed(1)}% CTR is excellent for Traffic! 🚀 ${clicks.toLocaleString()} clicks delivered efficiently.`;
            }
            return `${clicks.toLocaleString()} clicks at ${formatCurrency(campaign.cpc, campaign.currency)} CPC. For Traffic campaigns, CTR and CPC are what matter.`;
          } else if (objectiveCategoryKey === 'LEADS') {
            const leads = campaign.leads || 0;
            if (leads > 0) {
              const cpl = campaign.spend / leads;
              return `${leads} leads captured at ${formatCurrency(cpl, campaign.currency)} per lead. ${cpl <= getAdjustedThreshold(30, campaign.currency) ? 'Solid efficiency!' : 'Let\'s optimize that CPL.'}`;
            }
            return 'Lead gen campaign - focus on form completions and cost per lead.';
          }
          return `Analyzing ${objectiveCategory.toLowerCase()} metrics for optimization opportunities.`;
        })()}
      </p>
    </div>

    {/* Efficiency Analysis */}
    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
      <h4 className="text-blue-400 font-bold mb-2">💰 Cost Efficiency</h4>
      <p className="text-gray-300 text-sm">
        {(() => {
          const cpcEfficient = campaign.cpc <= getAdjustedThreshold(1.5, campaign.currency);
          const cpcModerate = campaign.cpc <= getAdjustedThreshold(2.5, campaign.currency);
          
          if (cpcEfficient) {
            return `CPC of ${formatCurrency(campaign.cpc, campaign.currency)} is excellent for your market! Your targeting and bidding are on point. 💪`;
          } else if (cpcModerate) {
            return `CPC of ${formatCurrency(campaign.cpc, campaign.currency)} is reasonable. Some room to optimize through audience refinement.`;
          }
          return `CPC of ${formatCurrency(campaign.cpc, campaign.currency)} is higher than ideal. Let's review targeting and bid strategy.`;
        })()}
      </p>
    </div>
  </div>
</div>
    
    {/* 6 Performance Analysis Cards - 2x3 Grid */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
     {/* CTR Analysis */}
      <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">📈 CTR Analysis</h3>
        <div className="mb-4">
          <div className="text-3xl font-bold text-white">{formatPercent(campaign.ctr)}</div>
          <div className={`text-sm font-medium ${
            campaign.ctr >= 2.0 ? 'text-green-500' : 
            campaign.ctr >= 1.0 ? 'text-yellow-500' : 
            'text-red-500'
          }`}>
            {campaign.ctr >= 2.0 ? '🚀 Excellent' : 
            campaign.ctr >= 1.0 ? '📊 Good' : 
            '📉 Needs Improvement'}
          </div>
        </div>
        <p className="text-gray-300 text-sm">
          {campaign.ctr >= 2.0
            ? 'Outstanding engagement rate indicates strong creative and targeting alignment.'
            : campaign.ctr >= 1.0
            ? 'Solid performance with room for optimization through creative testing.'
            : 'Below average CTR suggests creative fatigue or targeting misalignment.'}
        </p>
      </div>

      {/* Cost Efficiency */}
    <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
      <h3 className="text-xl font-bold text-green-400 mb-4">💰 Cost Efficiency</h3>
      <div className="mb-4">
        <div className="text-3xl font-bold text-white">
          {formatCurrency(campaign.cpc || 0, campaign.currency)}
        </div>
        <div className={`text-sm font-medium ${
          campaign.cpc <= getAdjustedThreshold(1.5, campaign.currency) ? 'text-green-500' : 
          campaign.cpc <= getAdjustedThreshold(2.5, campaign.currency) ? 'text-yellow-500' : 
          'text-red-500'
        }`}>
          {campaign.cpc <= getAdjustedThreshold(1.5, campaign.currency) ? '🎯 Efficient' : 
          campaign.cpc <= getAdjustedThreshold(2.5, campaign.currency) ? '⚖️ Moderate' : 
          '💸 Expensive'}
        </div>
      </div>
      <p className="text-gray-300 text-sm">
        {campaign.cpc <= getAdjustedThreshold(1.5, campaign.currency)
          ? 'Excellent cost efficiency. Campaign is delivering clicks at optimal rates.'
          : campaign.cpc <= getAdjustedThreshold(2.5, campaign.currency)
          ? 'Moderate costs. Consider audience refinement to improve efficiency.'
          : 'High acquisition costs detected. Review targeting and bid strategy.'}
      </p>
    </div>

      {/* Spend Analysis */}
      <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-4">💳 Spend Analysis</h3>
        <div className="mb-4">
          <div className="text-3xl font-bold text-white">{formatCurrency(campaign.spend || 0, campaign.currency)}</div>
          <div className={`text-sm font-medium ${
            displayWasteAmount <= 0 || (campaign.spend > 0 && (displayWasteAmount / campaign.spend) * 100 <= 10) ? 'text-green-500' : 
            campaign.spend > 0 && (displayWasteAmount / campaign.spend) * 100 <= 25 ? 'text-yellow-500' : 
            'text-red-500'
          }`}>
            {displayWasteAmount <= 0 || (campaign.spend > 0 && (displayWasteAmount / campaign.spend) * 100 <= 10) ? '💚 Efficient' : 
             campaign.spend > 0 && (displayWasteAmount / campaign.spend) * 100 <= 25 ? '🟡 Moderate Waste' : 
             '🔴 High Waste'}
          </div>
        </div>
        <p className="text-gray-300 text-sm">
          {displayWasteAmount <= 0 || (campaign.spend > 0 && (displayWasteAmount / campaign.spend) * 100 <= 10)
            ? `Excellent spend efficiency. ${campaign.spend > 0 && displayWasteAmount > 0 ? `Only ${((displayWasteAmount / campaign.spend) * 100).toFixed(1)}% potential optimization opportunity.` : 'No significant waste detected.'}`
            : campaign.spend > 0 && (displayWasteAmount / campaign.spend) * 100 <= 25
            ? `Moderate efficiency. ${((displayWasteAmount / campaign.spend) * 100).toFixed(1)}% of budget could be optimized.`
            : `Significant optimization needed. ${campaign.spend > 0 ? ((displayWasteAmount / campaign.spend) * 100).toFixed(1) : 0}% of budget is underperforming.`}
        </p>
        {displayWasteAmount > 0 && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-3">
            <p className="text-red-300 text-xs">
              💡 <strong>Waste Analysis:</strong> {formatCurrency(displayWasteAmount, campaign.currency)} potential savings identified ({campaign.spend > 0 ? ((displayWasteAmount / campaign.spend) * 100).toFixed(1) : 0}% of spend)
            </p>
          </div>
        )}
      </div>

     {/* Conversion Performance - Objective Aware */}
<div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
  <h3 className="text-xl font-bold text-green-400 mb-4">🎯 {
    objectiveCategoryKey === 'MESSAGES' ? 'Conversation Performance' :
    objectiveCategoryKey === 'TRAFFIC' ? 'Click Performance' :
    objectiveCategoryKey === 'LEADS' ? 'Lead Performance' :
    'Conversion Performance'
  }</h3>
  <div className="mb-4">
    <div className="text-2xl font-bold text-white">
      {(() => {
        // Show the RIGHT metric based on objective
        if (objectiveCategoryKey === 'MESSAGES') {
          return (campaign.messaging_conversations || campaign.success_metric_value || 0).toLocaleString();
        } else if (objectiveCategoryKey === 'TRAFFIC') {
          return (campaign.link_clicks || campaign.clicks || 0).toLocaleString();
        } else if (objectiveCategoryKey === 'LEADS') {
          return (campaign.leads || 0).toLocaleString();
        } else if (objectiveCategoryKey === 'SALES') {
          return (campaign.purchases || 0).toLocaleString();
        }
        return conversions?.toLocaleString() || '0';
      })()}
    </div>
    <div className={`text-sm font-medium ${
      (() => {
        // Evaluate based on objective - don't penalize MESSAGE campaigns for 0 conversions!
        if (objectiveCategoryKey === 'MESSAGES') {
          const convos = campaign.messaging_conversations || campaign.success_metric_value || 0;
          if (convos > 20) return 'text-green-500';
          if (convos > 5) return 'text-yellow-500';
          if (convos > 0) return 'text-yellow-500';
          return 'text-gray-400'; // Not red - 0 convos might just mean data isn't tracked
        } else if (objectiveCategoryKey === 'TRAFFIC') {
          const clicks = campaign.link_clicks || campaign.clicks || 0;
          if (clicks > 500) return 'text-green-500';
          if (clicks > 100) return 'text-yellow-500';
          return 'text-red-500';
        } else {
          if (conversions > 20) return 'text-green-500';
          if (conversions > 5) return 'text-yellow-500';
          return 'text-red-500';
        }
      })()
    }`}>
      {(() => {
        if (objectiveCategoryKey === 'MESSAGES') {
          const convos = campaign.messaging_conversations || campaign.success_metric_value || 0;
          if (convos > 20) return '🏆 Excellent';
          if (convos > 5) return '📊 Good';
          if (convos > 0) return '📈 Building';
          return '📱 Tracking Pending';
        } else if (objectiveCategoryKey === 'TRAFFIC') {
          const clicks = campaign.link_clicks || campaign.clicks || 0;
          if (clicks > 500) return '🏆 Excellent';
          if (clicks > 100) return '📊 Good';
          return '📈 Building';
        } else {
          if (conversions > 20) return '🏆 Excellent';
          if (conversions > 5) return '📊 Average';
          return '⚠️ Below Average';
        }
      })()}
    </div>
  </div>
  
  {/* Objective-specific explanation */}
  <p className="text-gray-300 text-sm">
    {(() => {
      if (objectiveCategoryKey === 'MESSAGES') {
        const convos = campaign.messaging_conversations || campaign.success_metric_value || 0;
        if (convos > 20) return `Great conversation volume! ${convos} people started chatting with you.`;
        if (convos > 0) return `${convos} conversations started. This is a Messages campaign - website conversions aren't the goal here.`;
        return 'This is a Messages campaign optimized for conversations, not website conversions. Conversation data may take time to sync.';
      } else if (objectiveCategoryKey === 'TRAFFIC') {
        const clicks = campaign.link_clicks || campaign.clicks || 0;
        if (clicks > 500) return `Strong click volume! ${clicks.toLocaleString()} people clicked through to your destination.`;
        if (clicks > 100) return `${clicks.toLocaleString()} clicks delivered. This is a Traffic campaign - focus on CTR and CPC.`;
        return 'Building click volume. This is a Traffic campaign - website conversions aren\'t the primary metric.';
      } else if (objectiveCategoryKey === 'LEADS') {
        const leads = campaign.leads || 0;
        if (leads > 20) return `Excellent lead generation! ${leads} leads captured.`;
        if (leads > 0) return `${leads} leads generated. Focus on cost per lead for optimization.`;
        return 'No leads yet. Review form setup and targeting.';
      }
      return conversions === 0 
        ? 'No conversions detected. Review tracking setup and landing page experience.'
        : `${conversions} conversions achieved.`;
    })()}
  </p>
</div>
{/* Cost per Success Metric - Only show if relevant */}
{(objectiveCategoryKey === 'MESSAGES' || objectiveCategoryKey === 'LEADS' || objectiveCategoryKey === 'SALES') && (
  <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
    <h3 className="text-xl font-bold text-green-400 mb-4">💵 Cost per {
      objectiveCategoryKey === 'MESSAGES' ? 'Conversation' :
      objectiveCategoryKey === 'LEADS' ? 'Lead' :
      'Purchase'
    }</h3>
    <div className="mb-4">
      <div className="text-3xl font-bold text-white">
        {(() => {
          const metric = objectiveCategoryKey === 'MESSAGES' 
            ? (campaign.messaging_conversations || campaign.success_metric_value || 0)
            : objectiveCategoryKey === 'LEADS'
            ? (campaign.leads || 0)
            : (campaign.purchases || 0);
          
          if (metric === 0) return 'N/A';
          const costPer = campaign.spend / metric;
          return formatCurrency(costPer, campaign.currency);
        })()}
      </div>
      <div className={`text-sm font-medium ${
        (() => {
          const metric = objectiveCategoryKey === 'MESSAGES' 
            ? (campaign.messaging_conversations || campaign.success_metric_value || 0)
            : objectiveCategoryKey === 'LEADS'
            ? (campaign.leads || 0)
            : (campaign.purchases || 0);
          
          if (metric === 0) return 'text-gray-400';
          const costPer = campaign.spend / metric;
          
          // Currency-aware thresholds
          const excellentThreshold = getAdjustedThreshold(
            objectiveCategoryKey === 'MESSAGES' ? 3 : objectiveCategoryKey === 'LEADS' ? 25 : 50,
            campaign.currency
          );
          const goodThreshold = getAdjustedThreshold(
            objectiveCategoryKey === 'MESSAGES' ? 8 : objectiveCategoryKey === 'LEADS' ? 50 : 100,
            campaign.currency
          );
          
          if (costPer <= excellentThreshold) return 'text-green-500';
          if (costPer <= goodThreshold) return 'text-yellow-500';
          return 'text-red-500';
        })()
      }`}>
        {(() => {
          const metric = objectiveCategoryKey === 'MESSAGES' 
            ? (campaign.messaging_conversations || campaign.success_metric_value || 0)
            : objectiveCategoryKey === 'LEADS'
            ? (campaign.leads || 0)
            : (campaign.purchases || 0);
          
          if (metric === 0) return '📊 Awaiting Data';
          const costPer = campaign.spend / metric;
          
          const excellentThreshold = getAdjustedThreshold(
            objectiveCategoryKey === 'MESSAGES' ? 3 : objectiveCategoryKey === 'LEADS' ? 25 : 50,
            campaign.currency
          );
          const goodThreshold = getAdjustedThreshold(
            objectiveCategoryKey === 'MESSAGES' ? 8 : objectiveCategoryKey === 'LEADS' ? 50 : 100,
            campaign.currency
          );
          
          if (costPer <= excellentThreshold) return '🎯 Excellent';
          if (costPer <= goodThreshold) return '⚖️ Moderate';
          return '💸 High';
        })()}
      </div>
    </div>
    <p className="text-gray-300 text-sm">
      {(() => {
        const metricName = objectiveCategoryKey === 'MESSAGES' ? 'conversation' : 
                          objectiveCategoryKey === 'LEADS' ? 'lead' : 'purchase';
        const metric = objectiveCategoryKey === 'MESSAGES' 
          ? (campaign.messaging_conversations || campaign.success_metric_value || 0)
          : objectiveCategoryKey === 'LEADS'
          ? (campaign.leads || 0)
          : (campaign.purchases || 0);
        
        if (metric === 0) return `No ${metricName}s tracked yet. Data will populate as they come in.`;
        
        const costPer = campaign.spend / metric;
        const excellentThreshold = getAdjustedThreshold(
          objectiveCategoryKey === 'MESSAGES' ? 3 : objectiveCategoryKey === 'LEADS' ? 25 : 50,
          campaign.currency
        );
        
        if (costPer <= excellentThreshold) {
          return `Great efficiency! You're acquiring ${metricName}s at an excellent rate.`;
        }
        return `Room to optimize. Let's work on bringing down the cost per ${metricName}.`;
      })()}
    </p>
  </div>
)}

      {/* Frequency Analysis - Only show if frequency exists */}
      {campaign.frequency !== null && campaign.frequency !== undefined && campaign.frequency > 0 && (
        <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-green-400 mb-4">🔄 Frequency Analysis</h3>
          <div className="mb-4">
            <div className="text-3xl font-bold text-white">{formatFrequency(campaign.frequency)}</div>
            <div className={`text-sm font-medium ${
              campaign.frequency <= 2.5 ? 'text-green-500' : 
              campaign.frequency <= 3.5 ? 'text-yellow-500' : 
              'text-red-500'
            }`}>
              {campaign.frequency <= 2.5 ? '✅ Optimal' : 
               campaign.frequency <= 3.5 ? '⚠️ Elevated' : 
               '🚨 Fatigue Risk'}
            </div>
          </div>
          <p className="text-gray-300 text-sm">
            {campaign.frequency <= 2.5 
              ? 'Healthy frequency level. Audience is not oversaturated.'
              : campaign.frequency <= 3.5
              ? 'Frequency approaching fatigue levels. Consider audience expansion.'
              : 'High frequency indicates audience fatigue. Expand targeting immediately.'}
          </p>
        </div>
      )}

      {/* ROAS - Only show for SALES campaigns with actual ROAS */}
      {shouldShowROAS(campaign.campaign_objective, campaign.roas, campaign.revenue) && (
        <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-green-400 mb-4">💵 Return on Ad Spend</h3>
          <div className="mb-4">
            <div className="text-3xl font-bold text-white">{safeNumber(campaign.roas, 2) || '0'}x</div>
            <div className={`text-sm font-medium ${
              campaign.roas >= 4 ? 'text-green-500' : 
              campaign.roas >= 2.5 ? 'text-yellow-500' : 
              'text-red-500'
            }`}>
              {campaign.roas >= 4 ? '🚀 Excellent' : 
               campaign.roas >= 2.5 ? '📊 Good' : 
               '⚠️ Below Target'}
            </div>
          </div>
          <p className="text-gray-300 text-sm">
            {campaign.roas >= 4 
              ? `Outstanding returns. Every ${formatCurrency(1, campaign.currency)} spent generates ${formatCurrency(campaign.roas, campaign.currency)} in revenue.`
              : campaign.roas >= 2.5
              ? 'Profitable campaign with room for optimization. Focus on scaling high-ROAS ad sets.'
              : 'ROAS below target. Review product pricing, audience targeting, and creative messaging.'}
          </p>
        </div>
      )}

      {/* Reach Analysis - Always show when reach data exists */}
{campaign.reach > 0 && (
  <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
    <h3 className="text-xl font-bold text-green-400 mb-4">👥 Reach Analysis</h3>
    <div className="mb-4">
      <div className="text-3xl font-bold text-white">{campaign.reach?.toLocaleString() || '0'}</div>
      <div className="text-sm font-medium text-blue-400">
        Unique Users Reached
      </div>
    </div>
    {campaign.frequency && campaign.frequency > 0 && (
      <div className="mb-4">
        <div className="text-2xl font-bold text-white">{formatFrequency(campaign.frequency)}</div>
        <div className="text-sm text-gray-400">Average Frequency</div>
      </div>
    )}
    <p className="text-gray-300 text-sm">
      {campaign.reach > 100000 
        ? `Excellent reach of ${(campaign.reach / 1000).toFixed(0)}K unique users. Campaign has broad audience exposure.`
        : campaign.reach > 10000
        ? `Good reach of ${(campaign.reach / 1000).toFixed(1)}K unique users. Consider expanding targeting for more reach.`
        : `Limited reach of ${campaign.reach?.toLocaleString()} users. Review audience size and targeting settings.`}
    </p>
  </div>
)}
    </div>

    {/* Performance History Chart */}
    {performanceHistory.length > 1 && (
      <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
        <h3 className="text-xl font-bold text-green-400 mb-6">
          📈 Performance Trend ({performanceHistory.length} days of data)
        </h3>
        <div className="mb-4">
          <div className="flex gap-4 justify-center flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-gray-400 text-sm">CTR</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
              <span className="text-gray-400 text-sm">CPC</span>
            </div>
            {performanceHistory.some(d => d.conversion_rate > 0) && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-gray-400 text-sm">Conv. Rate</span>
              </div>
            )}
          </div>
          <p className="text-gray-500 text-xs text-center mt-2">
            Tap/click bars for details
          </p>
        </div>
        
        {/* Chart Container with touch support */}
        <div className="flex items-end justify-between h-40 gap-1 px-2">
          {performanceHistory.slice(-30).map((day, index) => {
            const maxCtr = Math.max(...performanceHistory.map(d => d.ctr || 0), 5);
            const maxCpc = Math.max(...performanceHistory.map(d => d.cpc || 0), 5);
            const maxConv = Math.max(...performanceHistory.map(d => d.conversion_rate || 0), 10);
            
            const ctrHeight = maxCtr > 0 ? ((day.ctr || 0) / maxCtr) * 100 : 0;
            const cpcHeight = maxCpc > 0 ? ((day.cpc || 0) / maxCpc) * 100 : 0;
            const convHeight = maxConv > 0 ? ((day.conversion_rate || 0) / maxConv) * 100 : 0;
            
            const isSelected = selectedChartDay === index;
            
            return (
              <div 
                key={index} 
                className="flex flex-col items-center h-full flex-1 min-w-0 group relative cursor-pointer"
                onClick={() => handleChartInteraction(day, index)}
                onTouchEnd={(e) => { e.preventDefault(); handleChartInteraction(day, index); }}
              >
                {/* Tooltip - show on hover (desktop) or click (mobile) */}
                <div className={`absolute bottom-full mb-2 bg-gray-800 text-white text-xs p-2 rounded shadow-lg z-10 whitespace-nowrap ${
                  isSelected ? 'block' : 'hidden group-hover:block'
                }`}>
                  <div className="font-bold">{new Date(day.data_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  <div>CTR: {day.ctr?.toFixed(2)}%</div>
                  <div>CPC: {formatCurrency(day.cpc, campaign.currency)}</div>
                  {day.conversion_rate > 0 && <div>Conv: {day.conversion_rate?.toFixed(2)}%</div>}
                </div>
                
                <div className="flex items-end justify-center h-full w-full gap-0.5">
                  <div 
                    className={`bg-green-500 w-full max-w-[6px] rounded-t transition-all ${isSelected ? 'bg-green-300' : 'hover:bg-green-400'}`}
                    style={{ height: `${Math.max(ctrHeight, 2)}%` }}
                  />
                  <div 
                    className={`bg-orange-500 w-full max-w-[6px] rounded-t transition-all ${isSelected ? 'bg-orange-300' : 'hover:bg-orange-400'}`}
                    style={{ height: `${Math.max(cpcHeight, 2)}%` }}
                  />
                  {day.conversion_rate > 0 && (
                    <div 
                      className={`bg-blue-500 w-full max-w-[6px] rounded-t transition-all ${isSelected ? 'bg-blue-300' : 'hover:bg-blue-400'}`}
                      style={{ height: `${Math.max(convHeight, 2)}%` }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* X-axis labels */}
        <div className="flex justify-between mt-2 px-2">
          {performanceHistory.length > 0 && (
            <>
              <span className="text-xs text-gray-500">
                {new Date(performanceHistory[Math.max(0, performanceHistory.length - 30)]?.data_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <span className="text-xs text-gray-500">
                {new Date(performanceHistory[performanceHistory.length - 1]?.data_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </>
          )}
        </div>
        
        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-gray-700">
          <div className="text-center">
            <div className="text-sm text-gray-400">Avg CTR</div>
            <div className="text-lg font-bold text-green-400">
              {(performanceHistory.reduce((sum, d) => sum + (d.ctr || 0), 0) / performanceHistory.length).toFixed(2)}%
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-400">Avg CPC</div>
            <div className="text-lg font-bold text-orange-400">
              {formatCurrency(performanceHistory.reduce((sum, d) => sum + (d.cpc || 0), 0) / performanceHistory.length, campaign.currency)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-400">Days Tracked</div>
            <div className="text-lg font-bold text-blue-400">
              {performanceHistory.length}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* AI Performance Insights */}
    <div>
      <h3 className="text-xl font-bold text-green-400 mb-4">🤖 AI Performance Insights</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h4 className="text-blue-400 font-bold mb-2">🎯 Targeting Analysis</h4>
          <p className="text-gray-300 text-sm">
            {campaign.reach && campaign.impressions && campaign.reach > 0
              ? `Reaching ${campaign.reach.toLocaleString()} unique users with ${(campaign.impressions / campaign.reach).toFixed(1)}x average exposure. ${
                  campaign.frequency > 3 ? 'Audience pool too small - expand targeting.' : 'Healthy audience reach.'
                }`
              : campaign.impressions > 0
              ? `Campaign has delivered ${campaign.impressions.toLocaleString()} impressions. ${campaign.ctr > 1.5 ? 'Strong engagement indicates good targeting.' : 'Consider refining audience targeting for better engagement.'}`
              : 'Insufficient data for targeting analysis. Campaign needs more delivery.'}
          </p>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h4 className="text-blue-400 font-bold mb-2">📊 Benchmark Comparison</h4>
          <p className="text-gray-300 text-sm">
            {campaign.ctr > 0
              ? campaign.ctr > 1.2 
                ? `CTR ${Math.round((campaign.ctr / 1.2) * 100)}% above industry average (1.2%). Campaign creative resonates well with your audience.`
                : `CTR at ${Math.round((campaign.ctr / 1.2) * 100)}% of industry average (1.2%). Creative optimization recommended to improve engagement.`
              : 'CTR data not available for benchmark comparison.'}
          </p>
        </div>

        {performanceHistory.length > 7 && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h4 className="text-blue-400 font-bold mb-2">📈 Trend Analysis</h4>
            <p className="text-gray-300 text-sm">
              {(() => {
                const recent = performanceHistory.slice(-7);
                const earlier = performanceHistory.slice(-14, -7);
                if (recent.length === 0 || earlier.length === 0) return 'Insufficient data for trend analysis.';
                
                const recentAvgCtr = recent.reduce((sum, day) => sum + (day.ctr || 0), 0) / recent.length;
                const earlierAvgCtr = earlier.reduce((sum, day) => sum + (day.ctr || 0), 0) / earlier.length;
                
                if (earlierAvgCtr === 0) return recentAvgCtr > 0 ? 'Performance improving from zero baseline.' : 'Insufficient baseline data.';
                
                const ctrTrend = ((recentAvgCtr - earlierAvgCtr) / earlierAvgCtr) * 100;
                
                return ctrTrend > 5 
                  ? `Performance improving - CTR up ${ctrTrend.toFixed(1)}% over last week. Keep current strategy.`
                  : ctrTrend < -5
                  ? `Performance declining - CTR down ${Math.abs(ctrTrend).toFixed(1)}% over last week. Consider creative refresh.`
                  : 'Performance stable over the past week. Monitor for changes.';
              })()}
            </p>
          </div>
        )}

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <h4 className="text-blue-400 font-bold mb-2">🏢 Platform Insights</h4>
          <p className="text-gray-300 text-sm">
            {campaign.platform === 'facebook' 
              ? `Facebook algorithm optimization active. ${conversions >= 50 ? 'Campaign has enough conversions for optimal algorithm learning.' : 'Aim for 50+ conversions per week for best algorithm performance.'}`
              : campaign.platform === 'google'
              ? 'Google Ads Quality Score impacts costs. Focus on ad relevance and landing page experience.'
              : `${campaign.platform || 'Platform'} campaign optimization in progress.`}
          </p>
        </div>
      </div>
    </div>
  </div>
)}

          {activeTab === 'recommendations' && isActiveCampaign() && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-green-400 mb-2">🤖 AI Recommendations Engine</h2>
                <p className="text-gray-400 mb-6">
                  Actionable recommendations based on deep analysis
                </p>
              </div>
              
              {/* Priority Recommendations from Gemini */}
              {campaign.recommendations && campaign.recommendations.length > 0 ? (
                <div>
                  <h3 className="text-xl font-bold text-red-400 mb-4">🚨 AI-Generated Action Items</h3>
                  <div className="space-y-4">
                    {campaign.recommendations.map((rec, index) => (
                      <div key={index} className={`${
                        rec.priority === 'High' ? 'bg-red-500/10 border border-red-500/30' :
                        rec.priority === 'Medium' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                        'bg-blue-500/10 border border-blue-500/30'
                      } rounded-lg p-4 lg:p-6 flex gap-4`}>
                        <span className="text-3xl flex-shrink-0">
                          {rec.priority === 'High' ? '🚨' : rec.priority === 'Medium' ? '⚠️' : 'ℹ️'}
                        </span>
                        <div>
                          <h4 className={`${
                            rec.priority === 'High' ? 'text-red-400' :
                            rec.priority === 'Medium' ? 'text-yellow-400' :
                            'text-blue-400'
                          } font-bold text-lg mb-2`}>
                            {rec.category}: {rec.priority} Priority
                          </h4>
                          <p className="text-gray-300 mb-2">{rec.action}</p>
                          <p className="text-green-400 text-sm">
                            Expected Impact: {rec.expected_impact}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-bold text-red-400 mb-4">🚨 Immediate Actions Required</h3>
                  <div className="space-y-4">
                    {campaign.severity === 'CRITICAL' ? (
                      <>
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 lg:p-6 flex gap-4">
                          <span className="text-3xl flex-shrink-0">🛑</span>
                          <div>
                            <h4 className="text-red-400 font-bold text-lg mb-2">Critical Performance Issues</h4>
                            <p className="text-gray-300">
                              Campaign is significantly underperforming with CTR at {formatPercent(campaign.ctr)}. 
                              Immediate optimization required to prevent further budget loss.
                            </p>
                          </div>
                        </div>
                        <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 lg:p-6 flex gap-4">
                          <span className="text-3xl flex-shrink-0">🔧</span>
                          <div>
                            <h4 className="text-orange-400 font-bold text-lg mb-2">Required Optimizations</h4>
                            <ul className="text-gray-300 space-y-2">
                              <li>• Pause campaign and conduct full audit</li>
                              <li>• Refresh all creative assets with new angles</li>
                              <li>• Narrow audience targeting to high-intent segments</li>
                              <li>• Review and optimize landing page experience</li>
                            </ul>
                          </div>
                        </div>
                      </>
                    ) : campaign.severity === 'CHAMPION' ? (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 lg:p-6 flex gap-4">
                        <span className="text-3xl flex-shrink-0">🚀</span>
                        <div>
                          <h4 className="text-green-400 font-bold text-lg mb-2">Scale This Winning Campaign</h4>
                          <p className="text-gray-300 mb-3">
                            Exceptional performance detected! CTR: {formatPercent(campaign.ctr)}, CPC: {formatCurrency(campaign.cpc, campaign.currency)}
                          </p>
                          <ul className="text-gray-300 space-y-2">
                            <li>• Increase budget by 50-100% immediately</li>
                            <li>• Expand to lookalike audiences</li>
                            <li>• Test in new geographic markets</li>
                            <li>• Document winning formula for other campaigns</li>
                          </ul>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 lg:p-6 flex gap-4">
                        <span className="text-3xl flex-shrink-0">⚖️</span>
                        <div>
                          <h4 className="text-yellow-400 font-bold text-lg mb-2">Monitor and Optimize</h4>
                          <p className="text-gray-300">
                            Campaign performance is adequate but has room for improvement. 
                            Focus on incremental optimizations to improve efficiency.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Strategic Recommendations */}
              <div>
                <h3 className="text-xl font-bold text-blue-400 mb-4">🎯 Strategic Optimizations</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
                    <h4 className="text-yellow-400 font-bold mb-3">💰 Budget Optimization</h4>
                    <p className="text-gray-300 text-sm">
                      {campaign.severity === 'CHAMPION' 
                        ? `Outstanding performance justifies aggressive scaling. Increase budget by 50-100% while monitoring efficiency metrics.`
                        : campaign.severity === 'CRITICAL'
                        ? `Reduce budget by 50% immediately. Focus remaining budget on top-performing segments only.`
                        : displayWasteAmount > 0
                        ? `Waste of ${formatCurrency(displayWasteAmount, campaign.currency)} detected. Reallocate to better performing campaigns.`
                        : `Current budget allocation is reasonable. Monitor daily and adjust based on performance.`}
                    </p>
                  </div>
                  
                  <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
                    <h4 className="text-yellow-400 font-bold mb-3">🎨 Creative Strategy</h4>
                    <p className="text-gray-300 text-sm">
                      {campaign.frequency > 3.0 
                        ? `Frequency at ${formatFrequency(campaign.frequency)} indicates severe creative fatigue. Launch 3-5 new creative variations immediately.`
                        : campaign.ctr < 1.0
                        ? `Low CTR suggests poor creative performance. Test new hooks, value props, and visual styles.`
                        : `Creative performing well. Test iterative improvements while maintaining winning elements.`}
                    </p>
                  </div>
                  
                  <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
                    <h4 className="text-yellow-400 font-bold mb-3">👥 Audience Targeting</h4>
                    <p className="text-gray-300 text-sm">
                      {campaign.reach > 100000 && campaign.ctr < 1.0
                        ? `Large reach (${campaign.reach?.toLocaleString()}) with low engagement. Narrow targeting to improve relevance and reduce waste.`
                        : campaign.frequency > 3.0
                        ? `High frequency indicates audience exhaustion. Expand targeting by 50-100% to find new users.`
                        : campaign.ctr > 2.0
                        ? `Strong audience resonance. Create lookalike audiences based on converters.`
                        : `Review audience insights and refine targeting based on top performing segments.`}
                    </p>
                  </div>
                  
                  <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700">
                    <h4 className="text-yellow-400 font-bold mb-3">📈 Bidding Strategy</h4>
                    <p className="text-gray-300 text-sm">
                      {campaign.cpc > 3.0
                        ? `High CPC of ${formatCurrency(campaign.cpc, campaign.currency)} impacting efficiency. Switch to manual bidding with 20% lower max CPC.`
                        : campaign.conversion_rate > 0 && campaign.conversion_rate < 1.0
                        ? `Low conversion rate suggests bid optimization needed. Test Target CPA bidding.`
                        : campaign.severity === 'CHAMPION'
                        ? `Excellent efficiency allows for aggressive bidding. Increase bids 20% to capture more volume.`
                        : `Current bidding strategy is adequate. Monitor and adjust based on competition.`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Advanced Recommendations */}
              <div>
                <h3 className="text-xl font-bold text-purple-400 mb-4">🚀 Advanced Optimization Tactics</h3>
                <div className="space-y-4">
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <h4 className="text-purple-300 font-bold mb-2">📱 Platform-Specific Optimizations</h4>
                    <p className="text-gray-300 text-sm">
                      {campaign.platform === 'facebook'
                        ? 'Leverage Facebook\'s dynamic creative optimization. Upload 5-10 creative variations and let the algorithm find winners.'
                        : campaign.platform === 'google'
                        ? 'Implement responsive search ads with multiple headlines and descriptions. Use ad extensions to improve Quality Score.'
                        : `Optimize for ${campaign.platform || 'your platform'}\'s unique algorithm by following platform best practices.`}
                    </p>
                  </div>
                  
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <h4 className="text-purple-300 font-bold mb-2">🎯 Conversion Tracking Enhancement</h4>
                    <p className="text-gray-300 text-sm">
                      {conversions === 0
                        ? 'No conversions tracked. Verify pixel installation and implement enhanced conversion tracking (CAPI/Enhanced Conversions).'
                        : campaign.conversion_rate < 1.0
                        ? 'Low conversion rate. Implement micro-conversions to gather more optimization signals.'
                        : 'Conversion tracking operational. Consider value-based optimization for higher ROAS.'}
                    </p>
                  </div>
                  
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                    <h4 className="text-purple-300 font-bold mb-2">🔬 Testing Roadmap</h4>
                    <p className="text-gray-300 text-sm">
                      Priority tests for next 30 days:
                      {campaign.ctr < 1.5 && ' 1) New creative angles and formats.'}
                      {campaign.cpc > 2.0 && ' 2) Audience segmentation and exclusions.'}
                      {campaign.conversion_rate < 2.0 && ' 3) Landing page variations.'}
                      {campaign.frequency > 2.5 && ' 4) Broader targeting to reduce frequency.'}
                      {campaign.ctr >= 1.5 && campaign.cpc <= 2.0 && campaign.conversion_rate >= 2.0 && campaign.frequency <= 2.5 && ' Campaign well-optimized. Test scaling strategies.'}
                    </p>
                  </div>
                </div>
              </div>

              {/* AI Learning Insights */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 lg:p-6">
                <h3 className="text-purple-400 font-bold text-xl mb-4">🧠 AI Learning & Predictions</h3>
                
                <div className="mb-6">
                  <h4 className="text-purple-300 font-bold mb-3">📊 Confidence Assessment</h4>
                  <div className="mb-3">
                    <div className="w-full bg-gray-800 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-purple-600 to-purple-400 h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${geminiInsights?.confidence || 96}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm">
                    {geminiInsights?.confidence || 96}% confidence in recommendations based on comprehensive data analysis
                  </p>
                </div>
                
                <div>
                  <h5 className="text-purple-300 font-bold mb-3">Data Quality Factors:</h5>
                  <ul className="space-y-2 text-sm">
                    <li className="text-gray-300">
                      {campaign.spend > 100 ? '✅' : '⚠️'} Spend Volume: {formatCurrency(campaign.spend || 0, campaign.currency)} 
                      {campaign.spend > 500 ? ' (Excellent)' : campaign.spend > 100 ? ' (Sufficient)' : ' (Limited)'}
                    </li>
                    <li className="text-gray-300">
                      {performanceHistory.length > 7 ? '✅' : '⚠️'} Performance History: {performanceHistory.length} days
                      {performanceHistory.length > 14 ? ' (Comprehensive)' : performanceHistory.length > 7 ? ' (Good)' : ' (Limited)'}
                    </li>
                    <li className="text-gray-300">
                      ✅ Platform Data: {campaign.platform || 'Unknown'} (Full integration)
                    </li>
                    <li className="text-gray-300">
                      ✅ Objective Context: {objectiveCategory} (Analyzed)
                    </li>
                    <li className="text-gray-300">
                      {displayWasteAmount > 0 ? '⚠️' : '✅'} Waste Detection: {formatCurrency(displayWasteAmount, campaign.currency)} identified
                    </li>
                  </ul>
                </div>
                
                <div className="mt-6 bg-black/30 rounded-lg p-4">
                  <h5 className="text-purple-300 font-bold mb-2">🔮 Performance Prediction</h5>
                  <p className="text-gray-300 text-sm">
                    {campaign.severity === 'CRITICAL'
                      ? `Without immediate action, this campaign will continue to underperform. Estimated additional waste: ${formatCurrency((displayWasteAmount || 0) * 4, campaign.currency)} over the next 30 days if unchanged.`
                      : campaign.severity === 'CHAMPION'
                      ? `Scaling this campaign could generate ${Math.round(conversions * 2.5)} additional ${objectiveCategoryKey === 'LEADS' ? 'leads' : 'conversions'} at similar efficiency over the next 30 days.`
                      : `Implementing recommended optimizations could reduce waste by ${formatCurrency((displayWasteAmount || 0) * 0.5, campaign.currency)} and improve conversion rate by 15-25%.`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="bg-gray-900/80 rounded-xl p-4 lg:p-6 border border-gray-700 h-[70vh] flex flex-col">
              <h2 className="text-xl font-bold text-green-400 mb-4">🤖 Chat with Aura</h2>
              <div ref={chatContainerRef} className="flex-1 overflow-y-auto pr-4 space-y-4">
                {chatHistory.map((message, index) => (
                  <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-2xl p-4 rounded-xl ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-200'
                    }`}>
                      <div className="space-y-3">
                        {message.text.split('\n').map((line, lineIndex) => {
                          let formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                          
                          if (line.trim().startsWith('*') && !line.trim().startsWith('**')) {
                            formattedLine = '<span class="flex items-start gap-2"><span class="text-green-400 mt-1">•</span><span>' + 
                                          line.trim().substring(1).trim() + '</span></span>';
                          }
                          
                          if (line.trim().endsWith(':') && line.trim().length > 1) {
                            formattedLine = '<div class="font-bold text-green-400 mt-3 mb-1">' + formattedLine + '</div>';
                          }
                          
                          formattedLine = formattedLine.replace(/\[i\]/g, '');
                          
                          return (
                            <div 
                              key={lineIndex} 
                              dangerouslySetInnerHTML={{ __html: formattedLine }}
                              className={line.trim().startsWith('*') && !line.trim().startsWith('**') ? 'ml-4' : ''}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
                {isChatLoading && (
                  <div className="flex justify-start">
                    <div className="max-w-xl p-4 rounded-xl bg-gray-700 text-gray-200">
                      <div className="flex items-center gap-2">
                        <span className="animate-pulse">🤖</span>
                        <span>Aura is thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <form onSubmit={handleSendMessage} className="mt-4 flex gap-2">
                <input
                  type="text"
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  placeholder="Ask about performance, recommendations, or industry benchmarks..."
                  className="flex-1 bg-gray-800/80 border border-gray-600 rounded-lg px-4 py-3 text-white outline-none focus:border-green-500 transition-all"
                  disabled={isChatLoading}
                />
                <button
                  type="submit"
                  disabled={isChatLoading || !userMessage.trim()}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-500 transition-all disabled:opacity-50"
                >
                  Send
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignDeepAnalysis;